/**
 * Pasted links: one normalization the search bar and the server share.
 *
 * A shopper pastes whatever their browser or an app's share sheet gave them:
 * tracking parameters, someone else's affiliate tag, an Amazon `/ref=` tail, a
 * scheme-less `amazon.ca/...`. Normalizing the same way in the browser (to
 * dedupe chips) and on the server (to key the preview cache) means the two
 * always agree on what "the same link" is.
 *
 * Rules:
 *  - http and https only, no user:password, at most MAX_LINK_LENGTH chars;
 *    a scheme-less address that looks like a host gets https://.
 *  - Host lowercased (URL does it), fragment dropped.
 *  - Third-party affiliate and ad-click parameters dropped everywhere
 *    (stripThirdPartyParams). Other sites keep the rest of their query: a
 *    `ref` or `id` can be what names the product there.
 *  - Amazon product pages reduce to `https://www.<domain>[/<slug>]/dp/<ASIN>`:
 *    the slug before /dp/ is kept (it names the product when nothing else
 *    can be read), every parameter and `/ref=` tail goes, so someone else's
 *    `tag=` can never ride along. Our own tag is added only by the /go hop.
 *  - Amazon search pages keep `k` (and the department `i`) only.
 *
 * Client-safe: no Node APIs. Its imports are already in the search bar bundle.
 */

import { AMAZON_DOMAIN_TO_REGION_CODE, AmazonDomain } from "../types/amazon.type";
import { REGION_FOR_CODE, type Region, type RegionCode } from "../types/region.type";
import { amazonAsinPathMatch, ASIN_RE } from "./amazon-asin";
import { stripThirdPartyParams } from "./buy-link-market";

export const MAX_LINK_LENGTH = 2048;

/** Amazon's share-link hosts. They name no marketplace until followed. */
export const AMAZON_SHORT_HOSTS: ReadonlySet<string> = new Set(["a.co", "amzn.to", "amzn.eu", "amzn.asia", "amzn.com"]);

/** amazon.com, amazon.ca, amazon.co.uk, amazon.com.au, smile.amazon.com... never amazonaws.com. */
const AMAZON_MARKET_HOST = /^(?:[a-z0-9-]+\.)*amazon\.(?:[a-z]{2,3}|co\.[a-z]{2}|com\.[a-z]{2})$/;

/** Longest first, so amazon.com.au is never read as amazon.com. */
const AMAZON_DOMAINS: readonly AmazonDomain[] = [...Object.values(AmazonDomain)].sort((a, b) => b.length - a.length);

/** A dot and a real top-level label: "e.g." or "localhost" never reads as a link. */
const HAS_TLD = /\.(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})\.?$/i;

const SCHEMELESS_HOST = /^(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+\.?(?::\d+)?(?:[/?#]|$)/i;

export interface NormalizedLink {
  /** The normalized address. */
  url: string;
  /** Lowercase host without `www.`, for display ("amazon.ca", "bestbuy.ca"). */
  host: string;
}

export type LinkClass = "amazon-product" | "amazon-short" | "amazon-other" | "web";

export interface AmazonLinkParts {
  asin: string;
  domain: AmazonDomain;
  regionCode: RegionCode;
  region: Region;
  /** https://www.<domain>/dp/<ASIN>: no slug, no parameters. */
  cleanUrl: string;
}

export function bareHost(hostname: string): string {
  return hostname.toLowerCase().replace(/\.$/, "").replace(/^www\./, "");
}

export function isAmazonMarketHost(hostname: string): boolean {
  return AMAZON_MARKET_HOST.test(hostname.toLowerCase().replace(/\.$/, ""));
}

export function isAmazonShortHost(hostname: string): boolean {
  return AMAZON_SHORT_HOSTS.has(bareHost(hostname));
}

/** Any Amazon address a shopper could paste: a store page or a share link. */
export function isAmazonLinkHost(hostname: string): boolean {
  return isAmazonMarketHost(hostname) || isAmazonShortHost(hostname);
}

/** The AmazonDomain a host belongs to (smile.amazon.com is amazon.com), or null. */
export function amazonDomainOfHost(hostname: string): AmazonDomain | null {
  const host = hostname.toLowerCase().replace(/\.$/, "");
  for (const domain of AMAZON_DOMAINS) {
    if (host === domain || host.endsWith(`.${domain}`)) return domain;
  }
  return null;
}

function parseLoose(raw: string): URL | null {
  let text = raw.trim();
  if (!text) return null;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(text)) {
    if (!SCHEMELESS_HOST.test(text)) return null;
    text = `https://${text}`;
  }
  try {
    return new URL(text);
  } catch {
    return null;
  }
}

function isAmazonSearchPath(url: URL): boolean {
  return /^\/s\/?$/.test(url.pathname) || (url.pathname === "/" && url.searchParams.has("k"));
}

/** The words of an Amazon search page (`/s?k=garmin+fenix`), or "". */
export function amazonSearchTerms(href: string): string {
  try {
    const url = new URL(href);
    if (!isAmazonMarketHost(url.hostname)) return "";
    return (url.searchParams.get("k") ?? url.searchParams.get("field-keywords") ?? "").replace(/\s+/g, " ").trim();
  } catch {
    return "";
  }
}

/**
 * The normalized form of a pasted address, or null when it is not a web
 * address we would ever read (another scheme, credentials, too long).
 */
export function normalizeLinkUrl(raw: string | null | undefined): NormalizedLink | null {
  if (typeof raw !== "string" || raw.length > MAX_LINK_LENGTH * 2) return null;
  const url = parseLoose(raw);
  if (!url) return null;
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  if (url.username || url.password) return null;
  if (!HAS_TLD.test(url.hostname)) return null;
  url.hash = "";

  let out: string;
  if (isAmazonMarketHost(url.hostname)) {
    const domain = amazonDomainOfHost(url.hostname);
    const inPath = amazonAsinPathMatch(url.pathname);
    const queryAsin = (url.searchParams.get("asin") ?? url.searchParams.get("ASIN") ?? "").toUpperCase();
    const asin = inPath?.asin ?? (ASIN_RE.test(queryAsin) ? queryAsin : null);
    if (domain && asin) {
      // Keep a one-segment slug before the ASIN path ("/Garmin-Fenix-8/dp/...").
      const prefix = inPath ? url.pathname.slice(0, inPath.index) : "";
      const slug = /^\/[^/]+$/.test(prefix) && !/^\/(?:gp|exec|o)$/i.test(prefix) ? prefix : "";
      out = `https://www.${domain}${slug}/dp/${asin}`;
    } else if (isAmazonSearchPath(url)) {
      const kept = new URLSearchParams();
      const terms = url.searchParams.get("k") ?? url.searchParams.get("field-keywords");
      if (terms) kept.set("k", terms);
      const department = url.searchParams.get("i");
      if (department) kept.set("i", department);
      const query = kept.toString();
      out = `https://${url.host}/s${query ? `?${query}` : ""}`;
    } else {
      // Any other Amazon page: no parameter is worth keeping (tag, ref, ...).
      url.search = "";
      out = url.toString().replace(/\/ref=[^/]*$/, "");
    }
  } else {
    if (url.search === "?") url.search = "";
    out = stripThirdPartyParams(url.toString());
  }
  if (out.length > MAX_LINK_LENGTH) return null;
  return { url: out, host: bareHost(new URL(out).hostname) };
}

/** What kind of address this is. Expects a normalized URL. */
export function classifyLink(href: string): LinkClass {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return "web";
  }
  if (isAmazonShortHost(url.hostname)) return "amazon-short";
  if (isAmazonMarketHost(url.hostname)) return parseAmazonLink(href) ? "amazon-product" : "amazon-other";
  return "web";
}

/** The marketplace and ASIN of an Amazon product address, or null. */
export function parseAmazonLink(href: string): AmazonLinkParts | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (!isAmazonMarketHost(url.hostname)) return null;
  const domain = amazonDomainOfHost(url.hostname);
  if (!domain) return null;
  const inPath = amazonAsinPathMatch(url.pathname);
  const queryAsin = (url.searchParams.get("asin") ?? url.searchParams.get("ASIN") ?? "").toUpperCase();
  const asin = inPath?.asin ?? (ASIN_RE.test(queryAsin) ? queryAsin : null);
  if (!asin) return null;
  const regionCode = AMAZON_DOMAIN_TO_REGION_CODE[domain];
  return {
    asin,
    domain,
    regionCode,
    region: REGION_FOR_CODE[regionCode],
    cleanUrl: `https://www.${domain}/dp/${asin}`,
  };
}
